/**
 * ブラウザから FE サーバーのログ（`/api/client-log`）へ送るイベント（#459）。
 *
 * @remarks
 * ブラウザの出来事（画面の表示・画面のエラー・API の想定外の失敗）は、そのままではどこにも
 * 残らない。FE サーバーの route handler へ送り、サーバー側で接続元 IP と利用者（セッション）を
 * 付けて 1 行 1 JSON のログにする（`app/api/client-log/route.ts`。ブラウザの申告は信用しない）。
 * 送れなくても画面の動作は止めない（ログのための失敗をダイアログに出さない）。
 */
import type { ErrorReport } from "@/lib/errors/report";

/** 送り先（ALB は `/api/v1/*` 以外の `/api/*` を FE へ流す）。 */
export const CLIENT_LOG_ENDPOINT = "/api/client-log";

/** 1 件のスタックに入れる文字数の上限（keepalive の本文は 64KB まで）。 */
const MAX_STACK_LENGTH = 12_000;

/** 画面の表示。 */
export interface PageViewEvent {
  /** 種別。 */
  type: "page_view";
  /** 表示した画面（パス＋クエリ）。 */
  page: string;
  /** ブラウザの時刻（ISO 8601）。 */
  occurred_at: string;
}

/** 画面のエラー・API の想定外の失敗（ダイアログに出したもの）。 */
export interface ClientErrorEvent {
  /** 種別。 */
  type: "client_error";
  /** エラーの種類。 */
  kind: ErrorReport["kind"];
  /** 説明。 */
  message: string;
  /** 起きた画面。 */
  page: string;
  /** ブラウザの時刻（ISO 8601）。 */
  occurred_at: string;
  /** エラー ID（API・通信エラー）。 */
  request_id: string | null;
  /** 失敗したリクエスト（`GET /api/v1/…?…`）。 */
  request: string | null;
  /** HTTP ステータス。 */
  status: number | null;
  /** サーバーのエラーコード。 */
  code: string | null;
  /** 例外の型名。 */
  error_name: string | null;
  /** 画面のスタック（元の位置に戻せたらそれ）。 */
  stack: string | null;
  /** 元の位置に戻す前のスタック（戻せたときだけ）。 */
  stack_raw: string | null;
  /** サーバー側の描画の例外の digest。 */
  digest: string | null;
}

/** ブラウザから送るイベント。 */
export type ClientLogEvent = PageViewEvent | ClientErrorEvent;

const clip = (value: string | null): string | null =>
  value == null ? null : value.slice(0, MAX_STACK_LENGTH);

/**
 * ダイアログに出した 1 件をログのイベントにする。
 *
 * @param report - 1 件。
 */
export function clientErrorEvent(report: ErrorReport): ClientErrorEvent {
  const resolved = report.resolvedClientStack;
  return {
    type: "client_error",
    kind: report.kind,
    message: report.message,
    page: report.page,
    occurred_at: report.occurredAt.toISOString(),
    request_id: report.requestId,
    request: report.request ? `${report.request.method} ${report.request.url}` : null,
    status: report.status,
    code: report.code,
    error_name: report.errorName,
    stack: clip(resolved ?? report.clientStack),
    stack_raw: resolved ? clip(report.clientStack) : null,
    digest: report.digest,
  };
}

/**
 * イベントを FE サーバーへ送る（失敗は無視する）。
 *
 * @param event - 送るイベント。
 */
export function postClientLog(event: ClientLogEvent): void {
  try {
    void fetch(CLIENT_LOG_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
      credentials: "same-origin",
      // 画面を離れる直前のエラー・表示も送り切る
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // fetch が無い環境（テスト）では送らない
  }
}
